import { useCallback, useMemo } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import {
    CloseTemplateTaskColumn,
    CloseTemplateTaskRow,
} from "./closeTemplateTasksViewConfig";
import { LocalTask } from "./closeTemplateTasksDrawer.types";

interface UseCloseTemplateTasksDragAndDropParams {
    onLocalTasksChange: (updater: (prev: LocalTask[]) => LocalTask[]) => void;
    search: string;
}

export const useCloseTemplateTasksDragAndDrop = ({
    onLocalTasksChange,
    search,
}: UseCloseTemplateTasksDragAndDropParams) => {
    const handleRowDrop = useCallback(
        ({
            activeRow,
            overRow,
        }: {
            activeRow: CloseTemplateTaskRow;
            overRow: CloseTemplateTaskRow;
        }) => {
            const activeGroupId = activeRow.closeStageId ?? "__no_stage__";
            const overGroupId = overRow.closeStageId ?? "__no_stage__";
            if (activeGroupId !== overGroupId) {
                return;
            }

            onLocalTasksChange((prev) => {
                const allActive = [...prev]
                    .filter((t) => !t.isDeleted)
                    .sort((a, b) => a.order - b.order);

                const groupTasks = allActive.filter(
                    (t) => (t.closeStageId ?? "__no_stage__") === activeGroupId,
                );

                const sourceIndex = groupTasks.findIndex(
                    (t) => t.localId === activeRow.id,
                );
                const targetIndex = groupTasks.findIndex(
                    (t) => t.localId === overRow.id,
                );
                if (
                    sourceIndex === -1 ||
                    targetIndex === -1 ||
                    sourceIndex === targetIndex
                ) {
                    return prev;
                }

                const reorderedGroup = arrayMove(
                    groupTasks,
                    sourceIndex,
                    targetIndex,
                );

                let groupIdx = 0;
                const stitched = allActive.map((t) =>
                    (t.closeStageId ?? "__no_stage__") === activeGroupId
                        ? reorderedGroup[groupIdx++]
                        : t,
                );

                const updatedById = new Map(
                    stitched.map((t, idx) => [t.localId, { ...t, order: idx }]),
                );

                return prev.map((t) => updatedById.get(t.localId) ?? t);
            });
        },
        [onLocalTasksChange],
    );

    const canDragRow = useCallback(
        (row: CloseTemplateTaskRow) => !row.isGroupRow,
        [],
    );

    const getRowDragGroupId = useCallback(
        (row: CloseTemplateTaskRow) => row.closeStageId ?? "__no_stage__",
        [],
    );

    const rowDragAndDropConfig = useMemo(
        () => ({
            disabled: search.length > 0,
            canDragRow,
            getRowDragGroupId,
            onRowDrop: handleRowDrop,
            dragHandleColumnId: CloseTemplateTaskColumn.DRAG_HANDLE,
        }),
        [canDragRow, getRowDragGroupId, handleRowDrop, search],
    );

    return rowDragAndDropConfig;
};
